// Matching the names in an export against the names we already know.
//
// Two questions, one primitive. Is this export the conversation we already
// have — and which of these speakers is the person who exported it? Both
// come down to comparing names that WhatsApp wrote in two places and did not
// write identically, and both are answered carefully or not at all.
//
// Is this export the conversation we already have?
//
// A chat's identity is its title, and two exports of one group do not always
// write the title the same way. The group gets renamed; an iPhone export
// takes its name from the zip rather than from the transcript, and the share
// sheet is free to change that; someone saves the file with the year on the
// end. When the titles differ by a single character the archive gains a
// second row holding half the conversation, and nothing in the list says the
// two belong together.
//
// Nothing here decides anything. Titles that differ are allowed to be two
// different groups — "Obra 1" and "Obra 2" are not the same conversation, and
// no similarity measure can tell that pair apart from a rename. So this only
// says "these look related, worth asking about", and the person answers.

/** For comparing: case, accents, punctuation and emoji are not the name. */
export function normaliseTitle(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    // Emoji, flags, and the invisible characters that join them together.
    .replace(/[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{FE0F}\u{200D}]/gu, ' ')
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, ' ')
    .trim();
}

const words = (s: string) => new Set(normaliseTitle(s).split(' ').filter(Boolean));

/**
 * How much two titles have in common, from 0 to 1.
 *
 * Words rather than characters: a rename adds or drops whole words, and the
 * words that stay are the name.
 */
export function similarity(a: string, b: string): number {
  const x = words(a), y = words(b);
  if (!x.size || !y.size) return 0;
  let shared = 0;
  for (const w of x) if (y.has(w)) shared++;
  return shared / Math.max(x.size, y.size);
}

/**
 * Does the shorter name survive whole inside the longer one?
 *
 * Shared with working out whose messages are yours, where the same question
 * is asked of a person rather than a conversation: "Glenio" is the account
 * that wrote "Glênio Borges", and "Obra" is not the group "Obra 2".
 *
 * This is the shape of a rename: words get added — a year, a phase, "novo" —
 * and the ones that were there stay. A pair of different groups looks the
 * other way round: "Obra 1" and "Obra 2" share most of their words but each
 * has one the other lacks, and that one word is the whole distinction.
 *
 * Which makes it a much steadier test than a ratio. "Obras Tamboré UDI"
 * inside "Obras Tamboré UDI Fase 2" is three words of three; "Obra 1" and
 * "Obra 2" is one of two, and no threshold between those two ratios is
 * anything but a guess.
 */
export function containsName(a: string, b: string): boolean {
  const x = words(a), y = words(b);
  const [small, large] = x.size <= y.size ? [x, y] : [y, x];
  if (!small.size) return false;
  // The same name twice is the degenerate case, and it needs no caution:
  // "Ana" is Ana. The guard below is about a name *inside* a longer one,
  // and applying it to equality is what left every short first name — Ana,
  // Bo, Rafa, Mãe, the ones exports are full of — unrecognised.
  if (small.size === large.size && [...small].every((w) => large.has(w))) return true;
  // One word is only enough to go on when it is a word, not an initial.
  if (small.size === 1 && [...small][0]!.length < 6) return false;
  for (const w of small) if (!large.has(w)) return false;
  return true;
}

/**
 * The conversation an export is most likely a second copy of.
 *
 * An exact title is not in here: that one already lands on the same chat id
 * and merges without anybody being asked. This is only for the near misses,
 * and the answer is a question rather than an action.
 *
 * @param title what the export calls itself
 * @param candidates the conversations the archive already holds
 */
export function likelySameChat<T>(
  title: string, candidates: Array<{ chatId: string; title: string } & T>,
): ({ chatId: string; title: string } & T) | null {
  const key = normaliseTitle(title);
  if (key.length < 3) return null;

  let best: (typeof candidates)[number] | null = null;
  let score = 0;
  for (const c of candidates) {
    if (c.title === title) continue;             // already the same chat
    const alike = normaliseTitle(c.title) === key || containsName(title, c.title);
    if (!alike) continue;
    // Of several that qualify, the one that shares the most of its name:
    // "Damasceno" contains itself in "Familia Damasceno Borges!", but the
    // conversation somebody means is the one that is nearly all the same.
    const s = similarity(title, c.title);
    if (s > score) { score = s; best = c; }
  }
  return best;
}

import type { ParsedMessage } from './parser';

// ── Which of these names is you ────────────────────────────────────────────
//
// A WhatsApp export does not say who exported it. Every line is "Name:
// message", including your own, and nothing marks one of those names as the
// person holding the file. But the reader has to decide: messages you sent
// belong on the right, and the export's own order is no help — it used to be
// whoever spoke first, which put a builder's estimate on the right and your
// reply to it on the left.
//
// Three things are known that the file does not say, in order of how sure
// they are. Where none applies, nobody is you, every message is drawn as
// somebody else's with a name on it, and the conversation still reads.


/** Everyone who says anything, in the order they first do. */
export function speakers(messages: ParsedMessage[]): string[] {
  const seen = new Set<string>();
  for (const m of messages) if (m.sender) seen.add(m.sender);
  return [...seen];
}

/**
 * @param myName the account's own name, which is what an export labels your
 *   messages with — WhatsApp writes your profile name on your side of every
 *   conversation, so one name settles all of them.
 * @param title what the conversation is called. For a chat with one other
 *   person that is their name, which makes the other speaker you.
 */
export function ownerOf(
  messages: ParsedMessage[], myName?: string | null, title?: string,
): string | undefined {
  const who = speakers(messages);
  if (who.length === 0) return undefined;

  // 1 · The name you gave this archive. Tolerant of an accent and of a
  //     surname on one side only, but never ambiguous: if "Maria" could be
  //     either "Maria" or "Maria Beatriz", it settles nothing.
  if (myName && normaliseTitle(myName)) {
    const mine = who.filter((s) => containsName(s, myName));
    if (mine.length === 1) return mine[0];
  }

  // 2 · Two people, and one of them is what the conversation is called. A
  //     one-to-one export is named after the other person, so you are the
  //     one left over. This is the case the reader gets right without ever
  //     being told anything.
  if (who.length === 2 && title && normaliseTitle(title)) {
    const them = who.filter((s) => containsName(s, title));
    if (them.length === 1) return who.find((s) => s !== them[0]);
  }

  // 3 · Nothing here says. Better an even-handed conversation than a
  //     confident one with the wrong person down the right-hand side.
  return undefined;
}
